import * as THREE from 'three';

/**
 * World-space mission beacons: a ground ring plus a soft light column, so objectives read from the
 * high camera without hunting the minimap. Pooled; additive and depth-write free so they sit over
 * the night street like a sodium-lamp glow rather than a solid prop.
 */
export type BeaconStyle = 'objective' | 'giver' | 'vehicle';

/**
 * The marker scheme's colours (src/ui/markerScheme.ts): the job's next stop red like its diamond, people
 * with work green like their discs, the job's motor a pale lamp glow (its map marker is the red-edged one).
 */
const COLOURS: Record<BeaconStyle, number> = {
  objective: 0xe0452c,
  giver: 0x9fc65a,
  vehicle: 0xf0d27a,
};

function columnTexture(): THREE.Texture {
  const c = document.createElement('canvas');
  c.width = 4;
  c.height = 128;
  const g = c.getContext('2d')!;
  const grad = g.createLinearGradient(0, 0, 0, 128);
  grad.addColorStop(0, 'rgba(255,255,255,0)');
  grad.addColorStop(0.55, 'rgba(255,255,255,0.35)');
  grad.addColorStop(1, 'rgba(255,255,255,0.9)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 4, 128);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

interface Beacon {
  group: THREE.Group;
  ring: THREE.Mesh;
  column: THREE.Mesh;
  ringMat: THREE.MeshBasicMaterial;
  colMat: THREE.MeshBasicMaterial;
  phase: number;
}

export class BeaconPool {
  private readonly pool: Beacon[] = [];
  private used = 0;
  private time = 0;
  private readonly ringGeo = new THREE.RingGeometry(0.82, 1, 48);
  private readonly colGeo = new THREE.CylinderGeometry(0.62, 0.62, 1, 32, 1, true);
  private readonly tex = columnTexture();

  constructor(private readonly scene: THREE.Scene) {
    this.ringGeo.rotateX(-Math.PI / 2);
    this.colGeo.translate(0, 0.5, 0);
  }

  /** Call once per frame before `place`. */
  begin(dt: number): void {
    this.time += dt;
    this.used = 0;
  }

  place(x: number, z: number, radius: number, style: BeaconStyle): void {
    let b = this.pool[this.used];
    if (!b) {
      // Additive: face order can't change the sum, so one pass instead of three's back-then-front pair
      // (two draws and two program re-fetches per mesh per frame; docs/PERF.md "Phone pass").
      const ringMat = new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, forceSinglePass: true });
      const colMat = new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, map: this.tex, forceSinglePass: true });
      const ring = new THREE.Mesh(this.ringGeo, ringMat);
      const column = new THREE.Mesh(this.colGeo, colMat);
      ring.renderOrder = 5;
      column.renderOrder = 5;
      const group = new THREE.Group();
      group.add(ring, column);
      this.scene.add(group);
      b = { group, ring, column, ringMat, colMat, phase: this.pool.length * 1.7 };
      this.pool.push(b);
    }
    this.used++;
    const colour = COLOURS[style];
    b.ringMat.color.setHex(colour);
    b.colMat.color.setHex(colour);
    const pulse = 0.5 + 0.5 * Math.sin(this.time * 3 + b.phase);
    const r = Math.max(1.2, radius);
    b.group.visible = true;
    b.group.position.set(x, 0.07, z);
    b.ring.scale.setScalar(r * (0.92 + pulse * 0.1));
    b.ringMat.opacity = 0.55 + pulse * 0.35;
    const colR = style === 'vehicle' ? 0.9 : Math.min(r * 0.55, 2.6);
    const height = style === 'giver' ? 9 : style === 'vehicle' ? 6 : 16;
    b.column.scale.set(colR, height, colR);
    b.colMat.opacity = (style === 'vehicle' ? 0.22 : 0.3) + pulse * 0.12;
  }

  /** Hide unused beacons. */
  end(): void {
    for (let i = this.used; i < this.pool.length; i++) this.pool[i]!.group.visible = false;
  }

  dispose(): void {
    for (const b of this.pool) {
      this.scene.remove(b.group);
      b.ringMat.dispose();
      b.colMat.dispose();
    }
    this.ringGeo.dispose();
    this.colGeo.dispose();
    this.tex.dispose();
  }
}
